import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BuyerInfoModel, CardInfoModel } from '../models';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { GridListCardsComponent } from "../../../../features/find-property/grid-list-cards/grid-list-cards.component";
import { PortalApiService, PropertySummary } from 'src/app/services/portal-api.service';
import { Observable } from 'rxjs';
import { PropertyCardItemComponent } from "../../property-card-item/property-card-item.component";
import { OrderResponse, ProofOfPayment } from 'src/app/core/services/payments-api-service';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UnikoServicesConfig, UnikoServiceTypes } from 'src/app/services/payments-api.service';

@Component({
  selector: 'app-step-review',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, TranslocoPipe, LanguageRouterLinkDirective, ReactiveFormsModule],
  templateUrl: './step-review.component.html',
  styleUrl: './step-review.component.css'
})
export class StepReviewComponent implements OnInit, OnChanges {
  @Input() cardData: CardInfoModel | undefined;
  @Input() orderData: OrderResponse | undefined;
  @Input() isFromCompleteStep: boolean = false;
  @Output() formDataEvent = new EventEmitter<FormGroup>();
  protected propertySummary$: Observable<PropertySummary> | undefined;
  private propertyId?: string;
  protected ProofOfPaymentEnum = ProofOfPayment; //makes the enum available in the html template
  protected reviewForm!: FormGroup;
  protected termsDocumentType: string = 'services-document';

  private skuToTermsMap: Record<string, string> = { //This is a mapping of skus to cms' legal document codes
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.propertyUpload)?.sku!]: 'asset-onboarding',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.legalReport)?.sku!]: 'legal-report',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.technicalReport)?.sku!]: 'technical-report',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.preapproval)?.sku!]: 'property-certification',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.unikoExclusive)?.sku!]: 'uniko-exclusive',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.unikoPhotography)?.sku!]: 'uniko-exclusive',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.photography)?.sku!]: 'photography-service',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.propertyFileConstitution)?.sku!]: 'property-file-reconstitution',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.biddingSupport)?.sku!]: 'bidding-support',
  };
  constructor(
    private _portalApiService: PortalApiService,
    private _fb: FormBuilder
  ) {
  }

  ngOnInit(): void {
    // # reviewForm init
    this.reviewForm = this._fb.group({});

    this.propertyId = this.orderData?.orderItems?.at(0)?.propertyId ?? '';
    this.propertySummary$ = this._portalApiService.getPropertyById(this.propertyId).pipe();

    if (!this.isFromCompleteStep) {
      // # Upon checkout completion we don't add the termsConditionsAccepted form
      this.reviewForm.addControl('termsConditionsAccepted', new FormControl(false, Validators.requiredTrue));
      this.reviewForm.addControl('serviceTermsAccepted', new FormControl(false));
      if (this.orderData?.total !== 0) {
        this.reviewForm.addControl('paymentsTermsAccepted', new FormControl(false))
      }
      this.configureTreeDs();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderData'].isFirstChange()) {
      this.termsDocumentType = this.mapSkuToCmsDocumentType();
      console.log(this.termsDocumentType);
    }  
  }

  mapSkuToCmsDocumentType(): string {
    const skus = this.orderData?.orderItems?.map(m => m.sku);
    const cmsCodes = skus?.map(sku => {
      if (sku)
        return this.skuToTermsMap[sku];

      return null;
    }).filter(f => !!f);

    return cmsCodes?.at(0) ?? this.termsDocumentType;
  }

  public onSubmit(): boolean {
    if (!this.reviewForm) return false;

    this.reviewForm.markAllAsTouched();
    return this.reviewForm.valid;
  }

  public onTermsChange() {
    const service = this.reviewForm.get('serviceTermsAccepted')?.value ?? false;
    const payments = this.reviewForm.contains('paymentsTermsAccepted')
      ? this.reviewForm.get('paymentsTermsAccepted')?.value
      : true;

    this.reviewForm.get('termsConditionsAccepted')?.setValue(payments && service);
  }

  public controlHasError(formAndControlName: string): boolean {
    let result = false;
    const formControl = this.reviewForm.get(formAndControlName);
    const isInvalid = formControl ? !formControl.valid : false;
    const isTouched = formControl ? formControl.touched : false;
    result = isInvalid && isTouched;
    return result;
  }

  private configureTreeDs(): void {

    ThreeDS.configure({
      merchantId: this.cardData?.merchant,
      sessionId: this.cardData?.sessionId,
      containerId: "3DSUI",
      callback: () => {
        if (ThreeDS.isConfigured()) {
          this.initiateThreeDsAuthentication();
        } else {
          alert('error on 3Ds configuration')
        }
      },
      configuration: {
        wsVersion: Number(this.cardData?.version)
      }
    });
  }

  private initiateThreeDsAuthentication(): void {
    const orderNumber = this.orderData?.number ?? '';
    const transactionId = this.cardData?.transactionId ?? '';
    const optionalParams = {
      sourceOfFunds: {
        type: "CARD"
      },
    };

    ThreeDS.initiateAuthentication(orderNumber, transactionId, (data) => {
      if (data && data.error) {
        var error = data.error;

        //Something bad happened, the error value will match what is returned by the Authentication API
        console.error("error.code : ", error.code);
        console.error("error.msg : ", error.msg);
        console.error("error.result : ", error.result);
        console.error("error.status : ", error.status);
      } else {
        console.log("After Initiate 3DS ", data);

        //data.response will contain information like gatewayRecommendation, authentication version, and so on.
        console.log("REST API raw response ", data.restApiResponse);
        console.log("Correlation Id", data.correlationId);
        console.log("Gateway Recommendation", data.gatewayRecommendation);
        console.log("HTML Redirect Code", data.htmlRedirectCode);
        console.log("Authentication Version", data.authenticationVersion);

        switch (data.gatewayRecommendation) {
          case "PROCEED":
            if (data.restApiResponse.transaction.authenticationStatus != "AUTHENTICATION_AVAILABLE") {
              alert('data.restApiResponse.transaction.authenticationStatus != "AUTHENTICATION_AVAILABLE"')

            }
            //data.restApiResponse.transaction.authenticationStatus == "AUTHENTICATION_AVAILABLE"
            //ThreeDS.authenticatePayer(orderNumber, transactionId);//merchant's method
            //TODO: use paybody

            break;
          case "RESUBMIT_WITH_ALTERNATIVE_PAYMENT_DETAILS":
            //ThreeDS.tryOtherPayment();//Card does not support 3DS and transaction filtering rules require 3DS on this transaction: Ask the payer to select a different payment method.
            break;
        }
      }
    }, optionalParams);
  }
}
